import React, { useEffect, useRef } from "react";
import { Navigate, Outlet, useNavigate } from "react-router-dom";
import { all_routes } from "./all-routes";

/**
 * PrivateRoute
 * Ensures only authenticated users can access protected routes.
 * Redirects to login page if user is not authenticated or token is expired.
 * Continuously monitors token expiration in real-time.
 */
export const PrivateRoute: React.FC = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  // Don't check expiration here - let axios interceptor handle token refresh
  const isAuthenticated = Boolean(token);
  const expiryCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Only check if token exists, not if it's expired
    // The axios interceptor will handle token refresh automatically
    const checkTokenExists = () => {
      const currentToken = localStorage.getItem("token");
      
      if (!currentToken) {
        navigate(all_routes.login, { replace: true });
        return false;
      }
      return true;
    };

    // Initial check
    if (!checkTokenExists()) {
      return;
    }

    // Watch for token removal from other tabs or logout
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "token" && !e.newValue) {
        // Token was removed
        navigate(all_routes.login, { replace: true });
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      if (expiryCheckIntervalRef.current) {
        clearInterval(expiryCheckIntervalRef.current);
      }
    };
  }, [navigate]);

  return isAuthenticated ? <Outlet /> : null; // Avoid flash of private content
};

/**
 * PublicRoute
 * Blocks access to auth pages (login, register) when already logged in.
 * Redirects authenticated users to dashboard.
 * Checks token expiration and clears expired tokens.
 */
export const PublicRoute: React.FC = () => {
  const token = localStorage.getItem("token");
  
  // Only check if token exists - let axios interceptor handle expiration
  const isAuthenticated = Boolean(token);

  return !isAuthenticated ? (
    <Outlet />
  ) : (
    <Navigate to={all_routes.dashboard} replace />
  );
};
